import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { NavigateOptions, useLocation } from '@reach/router';

import { useNavigate } from '@lib/routing';
import { TabNavigation, TabsNavigation } from '@modules/layout/moleculas';
import { StudentGeneralTab } from './student-general-tab';
import { StudentPerformanceTab } from './student-performance-tab';

type StudentsTabsProps = {
    editRouteUrl: string;
};

const TabsContent = styled.div`
    width: 100%;
    min-height: 300px;
`;

export const studentTabs = [
    {
        hash: '#general',
        label: 'students:tabs.summary',
        Component: StudentGeneralTab,
    },
    {
        hash: '#performance',
        label: 'students:tabs.performance',
        Component: StudentPerformanceTab,
    },
];

const StudentTabs = (props: StudentsTabsProps): React.ReactElement | null => {
    const { editRouteUrl } = props;

    const { t } = useTranslation(['students']);

    const location = useLocation();
    const baseNavigate = useNavigate();

    const currentTabIdx = studentTabs.findIndex(tab => tab.hash === location.hash);

    const navigate = React.useCallback(
        (url: string, options: NavigateOptions<{}>) =>
            baseNavigate(url, { replace: true, ...options }),
        [baseNavigate],
    );

    const navigateWithState = (url: string) => {
        const backLinkState = { ...(location.state as Record<string, any>) };

        navigate(url, { state: backLinkState });
    };

    const handleChangeTab = (_: React.ChangeEvent<{}>, tab: number): void => {
        const currentStudentTab = studentTabs[tab];

        const tabLink = location.pathname + location.search + currentStudentTab.hash;

        navigateWithState(tabLink);
    };

    React.useEffect(() => {
        if (currentTabIdx === -1) {
            navigateWithState(studentTabs[0].hash);
        }
    }, [location]);

    React.useEffect(() => {
        if (currentTabIdx === 0 && studentTabs[0].hash !== location.hash) {
            navigateWithState(studentTabs[0].hash);
        }
    }, [studentTabs.length]);

    if (currentTabIdx === -1) {
        return null;
    }

    const { Component: TabComponent } = studentTabs[currentTabIdx];

    return (
        <>
            <TabsNavigation value={currentTabIdx} onChange={handleChangeTab}>
                {studentTabs.map(tab => (
                    <TabNavigation key={tab.hash} label={t(tab.label)} />
                ))}
            </TabsNavigation>

            {TabComponent ? (
                <TabsContent>
                    <TabComponent editRouteUrl={editRouteUrl} />
                </TabsContent>
            ) : null}
        </>
    );
};

export { StudentTabs };
